/**
 * Canonical platform and medium vocabulary for the whole Marketing module.
 *
 * Aligned with Zernio's published set (plus `'other'` for registry rows we
 * cannot classify). Aggregator research lists in `providers.ts` stay free-form
 * strings; this type is what SourceAccount / coverage / composer use.
 *
 * Accents use light-card-friendly fills (`bg-gray-900` rather than pure black
 * where it would disappear on dark badges).
 */

export type SocialPlatform =
  | 'x'
  | 'instagram'
  | 'facebook'
  | 'linkedin'
  | 'tiktok'
  | 'youtube'
  | 'threads'
  | 'pinterest'
  | 'reddit'
  | 'bluesky'
  | 'google_business'
  | 'telegram'
  | 'snapchat'
  | 'whatsapp'
  | 'discord'
  /** Unclassified / niche — tracked in the registry but not a first-class rail. */
  | 'other';

export type TraditionalMedium =
  | 'print'
  | 'online_news'
  | 'tv'
  | 'radio'
  | 'podcast'
  | 'wire'
  | 'other';

/**
 * Vendor aliases → our vocabulary. Applied at the UI edge so `twitter` from
 * Zernio and `google-business` from docs never leak into filters or badges.
 */
const PLATFORM_ALIASES: Record<string, SocialPlatform> = {
  twitter: 'x',
  'x.com': 'x',
  fb: 'facebook',
  ig: 'instagram',
  yt: 'youtube',
  'google-business': 'google_business',
  googlebusiness: 'google_business',
  gmb: 'google_business',
  'google my business': 'google_business',
  bsky: 'bluesky',
  'blue sky': 'bluesky',
};

/** Every named platform including `'other'` — form selects, filter chips. */
export const ALL_SOCIAL_PLATFORMS: SocialPlatform[] = [
  'x',
  'instagram',
  'facebook',
  'linkedin',
  'tiktok',
  'youtube',
  'threads',
  'pinterest',
  'reddit',
  'bluesky',
  'google_business',
  'telegram',
  'snapchat',
  'whatsapp',
  'discord',
  'other',
];

/** Platforms we can publish to (everything we name except `'other'`). */
export const PUBLISHABLE_PLATFORMS: SocialPlatform[] = ALL_SOCIAL_PLATFORMS.filter(
  (p) => p !== 'other',
);

export function isPublishable(platform: SocialPlatform): boolean {
  return platform !== 'other';
}

/** Normalize vendor / display strings into our SocialPlatform key. */
export function normalizeSocialPlatform(raw: string | null | undefined): SocialPlatform {
  const trimmed = String(raw || '').trim().toLowerCase();
  if (!trimmed) return 'other';
  const underscored = trimmed.replace(/[\s-]+/g, '_');
  const aliased = PLATFORM_ALIASES[trimmed] ?? PLATFORM_ALIASES[underscored];
  if (aliased) return aliased;
  if ((ALL_SOCIAL_PLATFORMS as string[]).includes(underscored)) {
    return underscored as SocialPlatform;
  }
  return 'other';
}

export const PLATFORM_LABEL: Record<SocialPlatform, string> = {
  x: 'X',
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  threads: 'Threads',
  pinterest: 'Pinterest',
  reddit: 'Reddit',
  bluesky: 'Bluesky',
  google_business: 'Google Business',
  telegram: 'Telegram',
  snapchat: 'Snapchat',
  whatsapp: 'WhatsApp',
  discord: 'Discord',
  other: 'Other',
};

export const PLATFORM_ACCENT: Record<SocialPlatform, string> = {
  x: 'bg-gray-900 text-white',
  instagram: 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 text-white',
  facebook: 'bg-[#1877F2] text-white',
  linkedin: 'bg-[#0A66C2] text-white',
  tiktok: 'bg-black text-white',
  youtube: 'bg-[#FF0000] text-white',
  threads: 'bg-black text-white',
  pinterest: 'bg-[#E60023] text-white',
  reddit: 'bg-[#FF4500] text-white',
  bluesky: 'bg-[#1185FE] text-white',
  google_business: 'bg-[#4285F4] text-white',
  telegram: 'bg-[#26A5E4] text-white',
  snapchat: 'bg-[#FFFC00] text-black',
  whatsapp: 'bg-[#25D366] text-white',
  discord: 'bg-[#5865F2] text-white',
  other: 'bg-gray-600 text-white',
};

/** Hex fills for Recharts series — keep in lockstep with `PLATFORM_ACCENT`. */
export const PLATFORM_CHART_COLOR: Record<SocialPlatform, string> = {
  x: '#111827',
  instagram: '#E1306C',
  facebook: '#1877F2',
  linkedin: '#0A66C2',
  tiktok: '#111111',
  youtube: '#FF0000',
  threads: '#1f1f1f',
  pinterest: '#E60023',
  reddit: '#FF4500',
  bluesky: '#1185FE',
  google_business: '#4285F4',
  telegram: '#26A5E4',
  snapchat: '#FFFC00',
  whatsapp: '#25D366',
  discord: '#5865F2',
  other: '#6b7280',
};

/**
 * Real per-platform caption limits, for the composer's live counters.
 *
 * `other` is `Infinity` rather than 0 or a guess: we do not publish there, so
 * there is no limit to enforce, and a 0 would make every draft look over-length.
 */
export const PLATFORM_CHAR_LIMIT: Record<SocialPlatform, number> = {
  x: 280,
  instagram: 2200,
  facebook: 63206,
  linkedin: 3000,
  tiktok: 2200,
  youtube: 5000,
  threads: 500,
  pinterest: 500,
  reddit: 40000,
  bluesky: 300,
  google_business: 1500,
  telegram: 4096,
  snapchat: 250,
  whatsapp: 4096,
  discord: 2000,
  other: Infinity,
};

export const MEDIUM_LABEL: Record<TraditionalMedium, string> = {
  print: 'Print',
  online_news: 'Online news',
  tv: 'TV',
  radio: 'Radio',
  podcast: 'Podcast',
  wire: 'Wire',
  other: 'Other',
};
